import { IonCol, IonContent, IonPage, IonRow, useIonRouter } from '@ionic/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ErrorMessage } from '@hookform/error-message'

import { useToast } from '../../contexts/toastContext'
import { useCreateClient } from '../../hooks/clients/mutations'
import { StyledError } from '../../components/ui/form/errors'
import { PhoneNumberInput, TextInput } from '../../components/ui/form/input'
import { StickyHeader } from '../../components/ui/sticky-header'
import { useClientInfo } from '../../clients/client-info-store'
import { useOptInClient } from '../../marketing/customer-opt-in/mutations'
import { addClientSchema } from '../../schema/add-client.schema'

import type { AddClientSchema } from '../../schema/add-client.schema'

type FormData = Pick<
  AddClientSchema,
  'firstName' | 'lastName' | 'phoneNumber' | 'email' | 'birthday'
>

function PairedAddCustomerPage() {
  return (
    <IonPage>
      <IonContent>
        <AddCustomer />
      </IonContent>
    </IonPage>
  )
}

function AddCustomer() {
  const router = useIonRouter()
  const setToast = useToast()

  const { phoneNumber, email } = useClientInfo()

  const createClient = useCreateClient()
  const optInClient = useOptInClient()

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    defaultValues: {
      phoneNumber: phoneNumber ?? '',
      firstName: '',
      lastName: '',
      email: email ?? '',
      birthday: { day: null, month: null },
    },
    resolver: zodResolver(
      addClientSchema.pick({
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        birthday: true,
      })
    ),
  })

  function handleFinish(formData: FormData) {
    createClient.mutate(formData, {
      onError: (error) =>
        setToast(
          `Something went wrong: ${
            error instanceof Error ? error.message : 'Unknown Error.'
          }`
        ),
      onSuccess: (data) => {
        // if customer got here from email check-in, do not opt-in
        if (email) return
        if (!phoneNumber) return

        optInClient.mutate(phoneNumber, {
          onSuccess: () =>
            router.push(
              `/check-in/paired/loyalty-info/${data.result.client.objectId}`,
              'none',
              'replace'
            ),
        })
      },
    })
  }

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center">
      <form onSubmit={handleSubmit(handleFinish)} className="w-full">
        <StickyHeader>
          <div className="flex justify-center py-6">
            <button
              disabled={createClient.isLoading}
              type="submit"
              color="secondary"
              className="bg-ion-color-secondary text-ion-color-primary rounded-full px-20 py-3 text-3xl font-semibold"
            >
              Continue
            </button>
          </div>
        </StickyHeader>

        <IonRow className="w-full">
          <IonCol size="6">
            <TextInput
              label="First Name (required)"
              disabled={createClient.isLoading}
              control={control}
              name="firstName"
            />
            <ErrorMessage name="firstName" as={StyledError} errors={errors} />
          </IonCol>
          <IonCol size="6">
            <TextInput
              label="Last Name"
              disabled={createClient.isLoading}
              control={control}
              name="lastName"
            />
          </IonCol>
        </IonRow>

        <IonRow className="w-full">
          <IonCol size="7">
            <TextInput
              disabled={createClient.isLoading}
              control={control}
              inputMode="email"
              name="email"
              label="Email"
            />
          </IonCol>
          <IonCol size="5">
            <PhoneNumberInput
              label="Phone Number"
              control={control}
              name="phoneNumber"
              placeholder="Phone Number"
              defaultCountry="US"
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol size="5">
            <div>
              <p className="font-semibold">Birthday</p>
              <div className="flex gap-x-3">
                <TextInput
                  inputMode="numeric"
                  control={control}
                  name="birthday.month"
                  label="Month"
                />
                <TextInput
                  inputMode="numeric"
                  control={control}
                  name="birthday.day"
                  label="Day"
                />
              </div>
              <ErrorMessage
                as={StyledError}
                errors={errors}
                name="birthday.month"
              />
              <ErrorMessage
                as={StyledError}
                errors={errors}
                name="birthday.day"
              />
            </div>
          </IonCol>
        </IonRow>

        <div className="py-4" />
        <p className="text-xl">
          We don't currently have you as a customer. Please fill out the form to
          join.
        </p>
      </form>
    </div>
  )
}

export { PairedAddCustomerPage }
